import { lazy, type ComponentType } from 'react';

type Mod<T> = { default: T };

/**
 * React.lazy that can be loaded ahead (speed.md 1, 3, decision 157). Once
 * preload() has the module, the lazy component resolves in the same render
 * (a thenable that answers at once), so a tap into the route never shows the
 * Suspense fallback: the route draws its own skeleton in the frame it opens.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own bound: a page takes any props
export function preloadable<T extends ComponentType<any>>(load: () => Promise<Mod<T>>) {
  let mod: Mod<T> | null = null;
  let pending: Promise<Mod<T>> | null = null;
  const preload = () => (pending ??= load().then((m) => (mod = m)));
  const Component = lazy(() => (mod
    ? ({ then: (resolve: (m: Mod<T>) => void) => resolve(mod!) } as unknown as Promise<Mod<T>>)
    : preload()));
  return Object.assign(Component, { preload });
}

/** Loads each when the browser is idle, one after the other, never in the way of the screen that asked. */
export function preloadWhenIdle(loads: Array<() => Promise<unknown>>): () => void {
  let stopped = false;
  const idle = (fn: () => void) => (typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback(fn, { timeout: 2_000 }) : setTimeout(fn, 200));
  const next = (i: number) => {
    if (stopped || i >= loads.length) return;
    idle(() => { if (!stopped) void loads[i]().catch(() => {}).finally(() => next(i + 1)); });
  };
  next(0);
  return () => { stopped = true; };
}
